/**
 * Organization formats (date, time, number, units, currency) plus the **viewer's** display
 * timezone. Formats come from the CRM organization setting (LLAMS-469), not per-user
 * preferences; `timeZone` remains the zone used to display stored UTC timestamps.
 *
 * Deliberately distinct from `CustomerLocale` (`src/document-render/domain/CustomerLocale.ts`),
 * which drives customer-facing document output and must NOT follow the viewer or organization
 * (LLAMS-327 FR-4.3a; LLAMS-469 D4). Same date/number field names on purpose so the shared
 * date/number pattern tables can serve both.
 */
export type ViewerDateFormat = 'UsMonthDayYear' | 'UkDayMonthYear' | 'IsoYearMonthDay';

export type ViewerNumberFormat = 'UsCommaDot' | 'EuropeanDotComma' | 'FrenchCanadianSpaceComma';

export type ViewerTimeFormat = 'TwelveHour' | 'TwentyFourHour';

export type ViewerMeasurementUnits = 'Imperial' | 'Metric';

export type ViewerLocale = {
  dateFormat: ViewerDateFormat;
  numberFormat: ViewerNumberFormat;
  /** IANA zone the viewer chose; timestamps are stored UTC and displayed here (FR-4.4). */
  timeZone: string;
  cultureTag: string;
  timeFormat: ViewerTimeFormat;
  measurementUnits: ViewerMeasurementUnits;
  /** ISO 4217 currency code (uppercased). */
  currency: string;
};

/**
 * Matches the PRD's FR-4.6 / LLAMS-469 US preset defaults.
 * `timeZone` is the organization fallback (`America/New_York`), not the browser zone —
 * the host pushes the resolved display zone; standalone / pre-push renders use this.
 */
export const DEFAULT_VIEWER_LOCALE: ViewerLocale = {
  dateFormat: 'UsMonthDayYear',
  numberFormat: 'UsCommaDot',
  timeZone: 'America/New_York',
  cultureTag: 'en-US',
  timeFormat: 'TwelveHour',
  measurementUnits: 'Imperial',
  currency: 'USD'
};

const DATE_FORMATS = new Set<string>(['UsMonthDayYear', 'UkDayMonthYear', 'IsoYearMonthDay']);
const NUMBER_FORMATS = new Set<string>(['UsCommaDot', 'EuropeanDotComma', 'FrenchCanadianSpaceComma']);
const TIME_FORMATS = new Set<string>(['TwelveHour', 'TwentyFourHour']);
const MEASUREMENT_UNITS = new Set<string>(['Imperial', 'Metric']);

/** Cached IANA identifiers — abbreviations like EST/PST are intentionally absent. */
let cachedIanaTimeZones: Set<string> | null = null;

function getIanaTimeZones(): Set<string> {
  if (cachedIanaTimeZones) return cachedIanaTimeZones;

  const zones = new Set<string>(['UTC']);
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      for (const zone of Intl.supportedValuesOf('timeZone')) {
        zones.add(zone);
      }
    }
  } catch {
    // Fall through — constructor probe below still covers common engines.
  }

  cachedIanaTimeZones = zones;
  return zones;
}

/**
 * True for canonical IANA zones (and UTC). Rejects abbreviations such as EST/PST
 * that some engines accept in `Intl.DateTimeFormat` but the epic bans.
 */
export function isValidIanaTimeZone(timeZone: string): boolean {
  const trimmed = timeZone.trim();
  if (!trimmed) return false;

  const known = getIanaTimeZones();
  if (known.size > 1) {
    return known.has(trimmed);
  }

  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    // Without supportedValuesOf, still reject common abbreviation forms.
    if (!trimmed.includes('/') && trimmed !== 'UTC') return false;
    return true;
  } catch {
    return false;
  }
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Validates a bag pushed across the federation boundary. Returns undefined for anything
 * malformed so the caller can fall back to the default rather than render garbage.
 *
 * `timeFormat`, `measurementUnits`, and `currency` are optional on the wire (host and remote
 * deploy independently — LLAMS-469) but always present on the resolved `ViewerLocale`. Missing
 * fields default; present-but-invalid values reject the whole payload.
 */
export function parseViewerLocale(raw: unknown): ViewerLocale | undefined {
  if (!isPlainObject(raw)) return undefined;

  const { dateFormat, numberFormat, timeZone, cultureTag, timeFormat, measurementUnits, currency } = raw;
  if (typeof dateFormat !== 'string' || !DATE_FORMATS.has(dateFormat)) return undefined;
  if (typeof numberFormat !== 'string' || !NUMBER_FORMATS.has(numberFormat)) return undefined;
  if (typeof timeZone !== 'string' || !isValidIanaTimeZone(timeZone)) return undefined;
  if (typeof cultureTag !== 'string' || !cultureTag.trim()) return undefined;

  let resolvedTimeFormat: ViewerTimeFormat = DEFAULT_VIEWER_LOCALE.timeFormat;
  if (timeFormat !== undefined) {
    if (typeof timeFormat !== 'string' || !TIME_FORMATS.has(timeFormat)) return undefined;
    resolvedTimeFormat = timeFormat as ViewerTimeFormat;
  }

  let resolvedMeasurementUnits: ViewerMeasurementUnits = DEFAULT_VIEWER_LOCALE.measurementUnits;
  if (measurementUnits !== undefined) {
    if (typeof measurementUnits !== 'string' || !MEASUREMENT_UNITS.has(measurementUnits)) return undefined;
    resolvedMeasurementUnits = measurementUnits as ViewerMeasurementUnits;
  }

  let resolvedCurrency = DEFAULT_VIEWER_LOCALE.currency;
  if (currency !== undefined) {
    if (typeof currency !== 'string' || !currency.trim()) return undefined;
    resolvedCurrency = currency.trim().toUpperCase();
  }

  return {
    dateFormat: dateFormat as ViewerDateFormat,
    numberFormat: numberFormat as ViewerNumberFormat,
    timeZone: timeZone.trim(),
    cultureTag: cultureTag.trim(),
    timeFormat: resolvedTimeFormat,
    measurementUnits: resolvedMeasurementUnits,
    currency: resolvedCurrency
  };
}
